import type { BackInStockSubscriptionsQuery } from '#graphql-operations'
import type { Composer } from 'vue-i18n'
import type { GuestBackInStockReminder, PendingBackInStock } from '../utils/backInStock'
import { computed, h, watch } from 'vue'
import { parseGuestBackInStockReminders, parsePendingBackInStock } from '../utils/backInStock'

const STORAGE_KEY = 'back-in-stock-pending'
const GUEST_STORAGE_KEY = 'back-in-stock-guest-reminders'

export function useBackInStock() {
  const auth = useAuth()
  const route = useRoute()
  const localePath = useLocalePath()
  const { locale, t } = useNuxtApp().$i18n as Composer
  const { toast } = useToast()
  const pending = useState<PendingBackInStock | null>('back-in-stock:pending', () => null)
  const busy = useState('back-in-stock:busy', () => false)
  const subscriptions = useState<BackInStockSubscriptionsQuery['backInStockSubscriptions']>('back-in-stock:list', () => [])
  const customerSubscribedIds = useState<string[]>('back-in-stock:subscribed', () => [])
  const guestReminders = useState<GuestBackInStockReminder[]>('back-in-stock:guest-reminders', () => [])
  const guestRestoreVersion = useState('back-in-stock:guest-restore-version', () => 0)
  const subscribedIds = computed(() => auth.loggedIn.value
    ? customerSubscribedIds.value
    : guestReminders.value.filter(reminder => reminder.locale === locale.value).map(reminder => reminder.variantId))
  const guestChoice = useState<(PendingBackInStock & { name?: string, label?: string }) | null>('back-in-stock:guest-choice', () => null)
  const guestError = useState('back-in-stock:guest-error', () => '')

  function persist(value: PendingBackInStock | null) {
    pending.value = value
    if (!import.meta.client)
      return
    try {
      if (value)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(value))
      else
        localStorage.removeItem(STORAGE_KEY)
    }
    catch {
      // The current tab can still complete sign-in when browser storage is unavailable.
    }
  }

  function restore() {
    try {
      pending.value = parsePendingBackInStock(localStorage.getItem(STORAGE_KEY))
    }
    catch {
      // Keep this tab's choice if storage is unavailable.
    }
    try {
      guestReminders.value = parseGuestBackInStockReminders(localStorage.getItem(GUEST_STORAGE_KEY))
      guestRestoreVersion.value++
    }
    catch {
      // The current tab still shows its successful requests without browser storage.
    }
  }

  function persistGuestReminders() {
    try {
      if (guestReminders.value.length)
        localStorage.setItem(GUEST_STORAGE_KEY, JSON.stringify(guestReminders.value))
      else
        localStorage.removeItem(GUEST_STORAGE_KEY)
    }
    catch {
      // Browser storage is optional; delivery remains tracked by the backend.
    }
  }

  function clearAvailable(variantIds: string[], market = locale.value) {
    const remaining = guestReminders.value.filter(reminder => reminder.locale !== market || !variantIds.includes(reminder.variantId))
    if (remaining.length !== guestReminders.value.length) {
      guestReminders.value = remaining
      persistGuestReminders()
    }
  }

  /** Reconcile cached browser hints with stock already loaded by the product surface. */
  function watchAvailability(availableVariantIds: () => string[]) {
    const availableIds = computed(availableVariantIds)
    let stockMarket = locale.value
    // Reconcile restored hints or changed stock, but keep new subscriptions that
    // the backend accepted even if another open surface still has older stock.
    watch([availableIds, guestRestoreVersion], ([ids], [previousIds]) => {
      // During a market change, the previous catalogue can remain rendered until
      // the next response arrives. Never apply that stock to the new market.
      if (ids !== previousIds)
        stockMarket = locale.value
      clearAvailable(ids, stockMarket)
    }, { immediate: true })
  }

  function bindEmail(emailAddress: string) {
    if (pending.value)
      persist({ ...pending.value, emailAddress: emailAddress.trim().toLowerCase() })
  }

  async function subscribe(variantId: string, returnTo: string) {
    const { data, errors } = await useGraphqlMutation('subscribeToBackInStock', { productVariantId: variantId })
    if (errors?.some((error: { message: string }) => error.message.includes('BACK_IN_STOCK_VARIANT_AVAILABLE'))) {
      // Stock can change while the customer signs in. End this intent and
      // send them to their exact variant instead of keeping an impossible retry.
      toast({ title: t('product.in_stock'), description: () => h('a', { href: returnTo, class: 'underline' }, t('general.details')), variant: 'success' })
      return
    }
    if (errors?.length || !data?.subscribeToBackInStock)
      throw new Error(errors?.[0]?.message ?? 'Subscription was not created')
    if (!customerSubscribedIds.value.includes(variantId))
      customerSubscribedIds.value = [...customerSubscribedIds.value, variantId]
    toast({ title: t('back_in_stock.subscribed'), variant: 'success' })
  }

  async function completePending() {
    const intent = pending.value
    if (!intent || busy.value || !auth?.loggedIn.value || intent.locale !== locale.value)
      return
    if (intent.emailAddress && intent.emailAddress !== auth.activeCustomer.value?.emailAddress.toLowerCase())
      return
    busy.value = true
    try {
      await subscribe(intent.variantId, intent.returnTo)
      if (pending.value?.variantId === intent.variantId)
        persist(null)
    }
    catch (error) {
      console.error('Failed to complete the back in stock notification', error)
      toast({ title: t('back_in_stock.error'), variant: 'destructive' })
    }
    finally {
      busy.value = false
    }
  }

  async function request(variantId: string, productHref?: string, product?: { name: string, label?: string }) {
    if (!variantId || busy.value)
      return
    busy.value = true
    try {
      // The current route already carries its locale. Card links can be bare
      // app paths or localized links; localize only the former, since this
      // router's localePath prefixes an already localized path a second time.
      const destination = new URL(productHref ?? route.fullPath, 'https://local.invalid')
      if (productHref && destination.pathname.split('/')[1] !== locale.value)
        destination.pathname = localePath(destination.pathname)
      destination.searchParams.delete('size')
      destination.searchParams.set('variant', variantId)
      const returnTo = `${destination.pathname}${destination.search}${destination.hash}`
      // The account lookup is deferred on cached product pages. A quick click must
      // await it before deciding that an already signed-in customer needs login.
      if (!auth.isAuthResolved.value || auth.isAuthLoading.value)
        await auth.refreshActiveCustomer()
      if (subscribedIds.value.includes(variantId))
        return
      guestError.value = ''
      guestChoice.value = { variantId, locale: locale.value, returnTo, ...product }
    }
    catch (error) {
      console.error('Failed to request a back in stock notification', error)
      toast({ title: t('back_in_stock.error'), variant: 'destructive' })
    }
    finally {
      busy.value = false
    }
  }

  function closeGuest() {
    if (busy.value)
      return
    guestChoice.value = null
    guestError.value = ''
  }

  async function confirmSubscription() {
    const intent = guestChoice.value
    if (!intent || busy.value || !auth.loggedIn.value || intent.locale !== locale.value)
      return
    guestError.value = ''
    busy.value = true
    try {
      await subscribe(intent.variantId, intent.returnTo)
      if (pending.value?.variantId === intent.variantId && pending.value.locale === intent.locale)
        persist(null)
      guestChoice.value = null
    }
    catch (error) {
      console.error('Failed to confirm a back in stock notification', error)
      guestError.value = t('back_in_stock.error')
    }
    finally {
      busy.value = false
    }
  }

  async function login() {
    const intent = guestChoice.value
    if (!intent || busy.value || intent.locale !== locale.value)
      return
    persist({ variantId: intent.variantId, locale: intent.locale, returnTo: intent.returnTo })
    closeGuest()
    await navigateTo({ path: localePath('/auth/login'), query: { redirectTo: intent.returnTo, notify: '1' } })
  }

  async function subscribeByEmail(emailAddress: string) {
    const intent = guestChoice.value
    if (!intent || busy.value || intent.locale !== locale.value)
      return
    guestError.value = ''
    busy.value = true
    try {
      const { data, errors } = await useGraphqlMutation('subscribeToBackInStockByEmail', {
        productVariantId: intent.variantId,
        emailAddress: emailAddress.trim().toLowerCase(),
      })
      if (errors?.some((error: { message: string }) => error.message.includes('BACK_IN_STOCK_VARIANT_AVAILABLE'))) {
        clearAvailable([intent.variantId], intent.locale)
        toast({ title: t('product.in_stock'), description: () => h('a', { href: intent.returnTo, class: 'underline' }, t('general.details')), variant: 'success' })
        guestChoice.value = null
        return
      }
      if (errors?.some((error: { message: string }) => error.message.includes('BACK_IN_STOCK_INVALID_EMAIL'))) {
        guestError.value = t('form.email.invalid')
        return
      }
      if (errors?.length || !data?.subscribeToBackInStockByEmail)
        throw new Error(errors?.[0]?.message ?? 'Subscription was not created')
      if (!guestReminders.value.some(reminder => reminder.variantId === intent.variantId && reminder.locale === intent.locale)) {
        guestReminders.value = [...guestReminders.value, { variantId: intent.variantId, locale: intent.locale }]
        persistGuestReminders()
      }
      guestChoice.value = null
      toast({ title: t('back_in_stock.subscribed'), variant: 'success' })
    }
    catch (error) {
      console.error('Failed to request a guest back in stock notification', error)
      guestError.value = t('back_in_stock.error')
    }
    finally {
      busy.value = false
    }
  }

  async function load() {
    const customerId = auth.activeCustomer.value?.id
    const requestedLocale = locale.value
    if (!customerId) {
      clearCustomer()
      return true
    }
    const { data, errors } = await useGraphqlQuery('backInStockSubscriptions')
    if (errors?.length || !data?.backInStockSubscriptions)
      throw new Error(errors?.[0]?.message ?? 'Notifications could not be loaded')
    if (customerId !== auth.activeCustomer.value?.id || requestedLocale !== locale.value)
      return true
    subscriptions.value = data.backInStockSubscriptions
    customerSubscribedIds.value = subscriptions.value.map(item => String(item.productVariantId))
    return true
  }

  async function cancel(id: string) {
    const { data, errors } = await useGraphqlMutation('cancelBackInStockSubscription', { id })
    if (errors?.length || !data?.cancelBackInStockSubscription)
      throw new Error(errors?.[0]?.message ?? 'Notification could not be cancelled')
    const removed = subscriptions.value.find(item => String(item.id) === id)
    subscriptions.value = subscriptions.value.filter(item => String(item.id) !== id)
    customerSubscribedIds.value = customerSubscribedIds.value.filter(variantId => variantId !== String(removed?.productVariantId))
    toast({ title: t('back_in_stock.cancelled'), variant: 'success' })
  }

  function clearCustomer() {
    subscriptions.value = []
    customerSubscribedIds.value = []
  }

  return { pending, busy, subscriptions, subscribedIds, guestChoice, guestError, closeGuest, login, confirmSubscription, subscribeByEmail, request, completePending, restore, bindEmail, load, cancel, clearCustomer, watchAvailability, clearPending: () => persist(null) }
}
